import React, { useState, useEffect, useMemo, useRef } from "react";
import debounce from "lodash.debounce";
import { useDispatch } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";
import * as Config from "../../Utils/Config";
import { lastFiveContract, getAllContracts } from "../../ReduxStore/Slice/Contract/contractSlice";
import { getAllQuotes } from "../../ReduxStore/Slice/Quote/CreateQuoteSlice";
import { roundNumber, getPaymentTermFrequency, getPriceChange, DateConvertWithTime } from "../../Utils/CommonFun";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers-pro/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs from "dayjs";
import { DatePicker } from "antd";

import { getDashboardData } from "../../ReduxStore/Slice/Dashboard/dashboardSlice";
const noDataImage = "/assets/img/no-data.png";

// NOTE: import these thunks yourself (you said you'll add them):
// import { lastFiveQuote } from "../../ReduxStore/Slice/Quote/CreateQuoteSlice";
// import { lastFiveContract } from "../../ReduxStore/Slice/Contract/contractSlice";

const formatCurrency = (val) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(val || 0);
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return null;
  return new Intl.NumberFormat("en-GB").format(Number(num));
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Accept':
      return 'accept'; // Using the existing 'accept' class (green)
    case 'Signed':
      return 'signed';
    case 'Pending':
      return 'pending'; // Using the existing 'pending' class (yellow)
    case 'Awaiting Signature':
      return 'awaitingsignature';
    case 'Reject':
    case 'Declined':
      return 'reject'; // Using the existing 'reject' class (red)
    case 'Sent':
      return 'sent'; // Using the existing 'sent' class (blue)
    default:
      return '';
  }
};

const paymentDivisorsFreguencyOngoing = {
  monthly_dd: 'DD',
  quarterly_dd: 'Quarterly DD',
  '100_upfront': 'Upfront',
  '50_upfront': '50-50',
  '100_post_completion': 'Upfront',
  manual_payment_monthly: "Non DD",
  manual_payment_quarterly: 'Non DD',
};

const CONTRACT_TYPE_LABELS = {
  contract_type_ongoing: "Ongoing",
  contract_type_oneoff: "One-off",
  contract_type_payg: "Pay As You Go",
};

const getContractTypeTags = (quote) => {
  const isOngoing = Number(quote?.recurring) === 1;
  const isOneOff = Number(quote?.one_off) === 1;
  const ongoingLabel = CONTRACT_TYPE_LABELS[quote?.contractTypeOngoing] ?? "Ongoing";
  const oneOffLabel = CONTRACT_TYPE_LABELS[quote?.contractTypeOneOff] ?? "One-off";

  if (isOngoing && isOneOff) {
    return ongoingLabel === oneOffLabel ? [ongoingLabel] : [ongoingLabel, oneOffLabel];
  }
  if (isOngoing) return [ongoingLabel];
  if (isOneOff) return [oneOffLabel];
  return [];
};

// Contract type label for a single service row (based on that service's own type, not the quote's overall tags)
const getServiceTypeLabel = (quote, serviceType) => {
  const isOngoingService = Number(serviceType) === 1;
  const key = isOngoingService ? quote?.contractTypeOngoing : quote?.contractTypeOneOff;
  const fallback = isOngoingService ? "Ongoing" : "One-off";
  return CONTRACT_TYPE_LABELS[key] ?? fallback;
};

// Status label + the simplified bucket the "Quote status" filter dropdown filters on
const getQuoteStatusInfo = (quote) => {
  const status = Number(quote?.status);

  if (status === 0) {
    if (Number(quote?.higher_discount_status) === 0 && Number(quote?.request_higher_discount_ongoing) === 1) {
      return { label: "Requested Higher Discount", bucket: "Pending" };
    }
    if (Number(quote?.higher_discount_status_oneoff) === 0 && Number(quote?.request_higher_discount_oneoff) === 1) {
      return { label: "Requested Higher Discount", bucket: "Pending" };
    }
    if (Number(quote?.higher_discount_status) === 1 || Number(quote?.higher_discount_status_oneoff) === 1) {
      return { label: "Discount Approved", bucket: "Pending" };
    }
    if (Number(quote?.higher_discount_status) === 2 || Number(quote?.higher_discount_status_oneoff) === 2) {
      return { label: "Discount Rejected", bucket: "Pending" };
    }
    return { label: "In Progress", bucket: "Pending" };
  }

  const label = { 1: "Accept", 2: "Reject", 3: "Pending" }[status] || "-";
  const bucket = label === "Accept" || label === "Reject" ? label : "Pending";
  return { label, bucket };
};

// Status label + the simplified bucket the "Contract status" filter dropdown filters on
const getContractStatusInfo = (quote) => {
  const contractSigned = Number(quote?.contract_signed);
  const status = Number(quote?.status);
  const step = Number(quote?.step);

  if (contractSigned === 1) {
    return { label: "Contract Signed", bucket: "Signed" };
  }
  if (status === 4) {
    return { label: "Contract Declined", bucket: "Declined" };
  }
  if (status === 3) {
    return { label: "Pending for Signature", bucket: "Awaiting Signature" };
  }
  if (step === 4) {
    return { label: "Pending for Signature", bucket: "Sent" };
  }

  if (Number(quote?.higher_discount_status) === 0 && Number(quote?.request_higher_discount_ongoing) === 1) {
    return { label: "Requested Higher Discount", bucket: null };
  }
  if (Number(quote?.higher_discount_status_oneoff) === 0 && Number(quote?.request_higher_discount_oneoff) === 1) {
    return { label: "Requested Higher Discount", bucket: null };
  }
  if (Number(quote?.higher_discount_status) === 1 || Number(quote?.higher_discount_status_oneoff) === 1) {
    return { label: "Discount Approved", bucket: null };
  }
  if (Number(quote?.higher_discount_status) === 2 || Number(quote?.higher_discount_status_oneoff) === 2) {
    return { label: "Discount Rejected", bucket: null };
  }

  return { label: "In Progress", bucket: null };
};

// Buckets the status filter dropdown offers per tab (single source of truth for both the <select> options and the filter)
const QUOTE_STATUS_BUCKETS = ["Accept", "Pending", "Reject"];
// Contract dropdown shows a friendly label, but getContractStatusInfo can produce more than one
// underlying bucket for the same label (e.g. "Pending for Signature" covers both "Awaiting Signature"
// and "Sent"), so each option lists every bucket value it should match.
const CONTRACT_STATUS_BUCKETS = [
  { label: "Contract Signed", buckets: ["Signed"] },
  { label: "Pending for Signature", buckets: ["Awaiting Signature", "Sent"] },
  { label: "Contract Declined", buckets: ["Declined"] },
];

// Whether a quote/contract row matches the selected status filter value
const matchesStatusFilter = (quote, tab, value) => {
  if (value === "All") return true;
  if (tab === "Quote") {
    return getQuoteStatusInfo(quote).bucket === value;
  }
  const option = CONTRACT_STATUS_BUCKETS.find((o) => o.label === value);
  return option ? option.buckets.includes(getContractStatusInfo(quote).bucket) : false;
};

const QUALITY_DATA_LABELS = {
  default: "Excellent",
  low: "Good",
  medium: "Average",
  high: "Poor",
};

const getQualityDataLabel = (value) => QUALITY_DATA_LABELS[value?.toLocaleLowerCase()] ?? value;

const stripHtml = (value) => value?.replace(/<[^>]*>/g, "").trim();

const withDefaultLabel = (value, replacement) =>
  value?.toLocaleLowerCase() === "default" ? replacement : value;

const PAGE_SIZE = 20; // per page records for infinite scroll

export default function QuoteReport() {
  const dispatch = useDispatch();
  const agent_id = JSON.parse(localStorage.getItem("agent_id"));

  const [currentDateFilter, setCurrentDateFilter] = useState("current-month");
  // Custom-date range picker value ([start, end] dayjs objects), only used when currentDateFilter === "custom-date"
  const [range, setRange] = useState(null);
  const [activeTab, setActiveTab] = useState("Quote");
  const [statusValue, setStatusValue] = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Text shown in the search box (updates immediately as the user types)
  const [searchTerm, setSearchTerm] = useState("");
  // Search sent to the API (req.search) — updated 500ms after typing stops
  const [InputValue, setInputValue] = useState("");

  // Data + pagination + loader
  const [lastFiveQuoteData, setLastFiveQuoteData] = useState([]);
  const [lastFiveContractData, setLastFiveContractData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  // Set to false once a page comes back short of PAGE_SIZE (or empty) — the definitive
  // "no more rows" signal, independent of pagination.total (which can be stale/mismatched)
  const [hasMore, setHasMore] = useState(true);

  // Independent loaders for the two export buttons
  const [csvExporting, setCsvExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const [cardData, setCardData] = useState([]);
  const [oldData, setOldData] = useState({});

  console.log("oldData", oldData)

  // Full (unpaginated) rows for the current tab/search, fetched whenever a status
  // filter other than "All" is selected — the status bucket isn't a backend column,
  // so filtering the already-loaded (paginated) rows would miss rows not yet scrolled to
  const [statusFilterData, setStatusFilterData] = useState([]);

  // ids of the rows returned by the most recent search — captured straight from the
  // debounced search's fetch response so it never lags behind what's actually on screen
  const [ids, setIds] = useState([]);

  console.log("cardData", cardData);
  console.log("oldData", oldData);

  // Rows currently shown depend on the active tab
  const tableData = activeTab === "Quote" ? lastFiveQuoteData : lastFiveContractData;














  // Tracks whether the in-flight fetch was triggered by typing in the search box,
  // so we can skip showing the loader for it (only tab-switch/scroll show the loader)
  const isSearchFetch = useRef(false);

  // Synchronous guard against a single scroll gesture firing multiple scroll events
  // before the async `loading` state has a chance to flip to true
  const isFetchingMore = useRef(false);

  // ---------------- API CALLS ----------------
  // Builds the {filter, startDate, endDate} bits shared by every report request
  // (list, summary cards, export) so they all stay filtered to the same date range
  const getDateFilterParams = () => {
    if (currentDateFilter === "custom-date") {
      if (Array.isArray(range) && range[0] && range[1]) {
        return {
          filter: currentDateFilter,
          startDate: dayjs(range[0]).format("YYYY-MM-DD"),
          endDate: dayjs(range[1]).format("YYYY-MM-DD"),
        };
      }
      return {};
    }
    return { filter: currentDateFilter };
  };

  const getLastFiveQuote = async () => {
    const wasSearchFetch = isSearchFetch.current;
    if (!isSearchFetch.current) setLoading(true);
    const req = {
      agent_id,
      page: currentPage,
      limit: PAGE_SIZE,
      search: InputValue,
      quoteTiles: "",
      type: "reports",
      ...getDateFilterParams(),
    };
    await dispatch(getAllQuotes(req)).unwrap()
      .then((response) => {
        if (response.status) {
          setLastFiveQuoteData((prev) =>
            currentPage === 1 ? response.data : [...prev, ...response.data]
          );
          setTotalRecords(response?.pagination?.total || 0);
          setHasMore((response?.data?.length || 0) >= PAGE_SIZE);

          if (wasSearchFetch && currentPage === 1) {
            setIds(
              InputValue?.trim()
                ? (response.data || []).map((row) => row.id).filter((id) => id != null)
                : []
            );
          }
        } else if (currentPage === 1) {
          setLastFiveQuoteData([]);
          setTotalRecords(0);
          setHasMore(false);

          if (wasSearchFetch) setIds([]);
        } else {
          setHasMore(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching quote data:", error);
      })
      .finally(() => {
        setLoading(false);
        isSearchFetch.current = false;
        isFetchingMore.current = false;
      });
  };

  const getLastFiveContract = async () => {
    const wasSearchFetch = isSearchFetch.current;
    if (!isSearchFetch.current) setLoading(true);
    const req = {
      agent_id,
      page: currentPage,
      limit: PAGE_SIZE,
      search: InputValue,
      contractTiles: "",
      type: "reports",
      ...getDateFilterParams(),
    };
    await dispatch(getAllContracts(req)).unwrap()
      .then((response) => {
        if (response.status) {
          setLastFiveContractData((prev) =>
            currentPage === 1 ? response.data : [...prev, ...response.data]
          );
          setTotalRecords(response?.pagination?.total || 0);
          setHasMore((response?.data?.length || 0) >= PAGE_SIZE);

          if (wasSearchFetch && currentPage === 1) {
            setIds(
              InputValue?.trim()
                ? (response.data || []).map((row) => row.id).filter((id) => id != null)
                : []
            );
          }
        } else if (currentPage === 1) {
          setLastFiveContractData([]);
          setTotalRecords(0);
          setHasMore(false);

          if (wasSearchFetch) setIds([]);
        } else {
          setHasMore(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching contract data:", error);
      })
      .finally(() => {
        setLoading(false);
        isSearchFetch.current = false;
        isFetchingMore.current = false;
      });
  };

  // Fetch whenever the tab, page (scroll), search, or date filter changes
  useEffect(() => {
    if (activeTab === "Quote") getLastFiveQuote();
    else getLastFiveContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, InputValue, currentDateFilter, range]);

  // Debounce the actual API search so it only fires 500ms after typing stops
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        isSearchFetch.current = true;
        setInputValue(value);
        setCurrentPage(1);
        setHasMore(true);
      }, 1000),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  // Change date filter -> reset to page 1 so the range switch doesn't append onto stale pages
  const handleDateChange = (event) => {
    const value = event.target.value;
    if (value === currentDateFilter) return;
    setCurrentDateFilter(value);
    if (value !== "custom-date") setRange(null);
    setCurrentPage(1);
    setHasMore(true);
  };

  // Custom date range picked -> reset to page 1 the same way
  const handleRangeChange = (dates) => {
    setRange(dates);
    setCurrentPage(1);
    setHasMore(true);
  };

  // Switch tab -> reset everything and load fresh (page 1) for that tab
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    debouncedSearch.cancel();
    isSearchFetch.current = false;
    isFetchingMore.current = false;
    setActiveTab(tab);
    setStatusValue("All");
    setStatusFilterData([]);
    setSearchTerm("");
    setInputValue("");
    setExpandedId(null);
    setCurrentPage(1);
    setLastFiveQuoteData([]);
    setLastFiveContractData([]);
    setHasMore(true);
    setIds([]);
  };

  // Search -> update the box immediately, debounce the API call
  const handleSearch = (value) => {
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Infinite scroll: near the bottom -> load next page (10 more)
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      !isFetchingMore.current &&
      !loading &&
      hasMore &&
      scrollHeight - scrollTop - clientHeight < 60 &&
      tableData.length < totalRecords
    ) {
      isFetchingMore.current = true;
      setCurrentPage((prev) => prev + 1);
    }
  };

  const toggleQuote = async (PackageServices, id) => {

    if (PackageServices?.length === 0) return; // matches original: no expanded row available for these
    setExpandedId((prev) => (prev === id ? null : id));


    /////////////

    let req = { agent_id: agent_id, type: activeTab, link: "single-details", id: id };
    await dispatch(getDashboardData(req))
      .unwrap()
      .then((response) => {
        if (response.status) {
          setOldData(response.data);
        } else {
          setOldData({});
        }
      })
      .catch((error) => {
        return
      });


  };

  // De-dupe rows by id, then apply the status filter, before rendering.
  // When a status filter is active, filter against the fully-fetched statusFilterData
  // (not the paginated tableData) so rows not yet scrolled into view aren't missed.
  const uniqueTableData = useMemo(() => {
    const source = statusValue === "All" ? (tableData || []) : (statusFilterData || []);

    const seen = new Set();
    const deduped = source.filter((quote) => {
      if (seen.has(quote.id)) return false;
      seen.add(quote.id);
      return true;
    });

    if (statusValue === "All") return deduped;

    return deduped.filter((quote) => matchesStatusFilter(quote, activeTab, statusValue));
  }, [tableData, statusValue, activeTab, statusFilterData]);

  // IDs to scope the summary cards to: when a status filter is active, scope to whatever
  // rows that filter matches (mirrors uniqueTableData); otherwise fall back to the
  // search-driven ids so the cards keep matching the search results.
  const dashboardIds = useMemo(() => {
    if (statusValue === "All") return ids;
    return uniqueTableData.map((quote) => quote.id).filter((id) => id != null);
  }, [statusValue, ids, uniqueTableData]);

  // CardData
  const DashboardData = async () => {
    //setLoading(true);
    let req = { agent_id: agent_id, type: activeTab, link: "report", ids: dashboardIds, ...getDateFilterParams() };

    await dispatch(getDashboardData(req))
      .unwrap()
      .then((response) => {
        if (response.status) {
          setCardData(response.data);
        } else {
          setCardData([]);
          //setLoading(false);
        }
      })
      .catch((error) => {
        // setLoading(false);
        console.log(error);
      });
  };

  useEffect(() => {
    DashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dashboardIds, currentDateFilter, range]);

  // Derives the exact values shown in each sortable column, so sorting matches what's on screen
  const getRowMetrics = (quote) => {
    const PackageServices = activeTab === "Quote" ? quote?.QuotePackageServices : quote?.ContractPackageServices;

    const totalamount = PackageServices?.reduce((sum, item) => (
      Number(item?.service_type) !== 1 ? sum : sum + (item?.price)
    ), 0) || 0;
    const totalamountWithDiscount = totalamount > 0 ? (totalamount - (totalamount * quote?.discount_ongoing) / 100) : 0;

    const totalamountOneOff = PackageServices?.reduce((sum, item) => (
      Number(item?.service_type) !== 0 ? sum : sum + (item?.price)
    ), 0) || 0;
    const totalamountWithDiscountOneOff = totalamountOneOff > 0 ? (totalamountOneOff - (totalamountOneOff * quote?.discount_oneoff) / 100) : 0;

    const net = roundNumber(totalamountWithDiscount) + roundNumber(totalamountWithDiscountOneOff);

    const subtotalValue = PackageServices?.reduce((sum, item) => sum + (item?.price), 0) || 0;
    const discount = roundNumber(subtotalValue) - net;

    const total = quote?.Agent?.vat_registered?.toUpperCase() === "YES"
      ? net + roundNumber(net * 0.20)
      : net;

    const hasNullPackage = PackageServices?.some((item) => item?.package_id === null);
    const packageItem = PackageServices?.find((item) => item?.Package);
    const packageName = (hasNullPackage || Number(quote?.package_change_status) === 1)
      ? "Custom Package"
      : (packageItem?.Package?.name || "-");

    const statusLabel = activeTab === "Quote" ? getQuoteStatusInfo(quote).label : getContractStatusInfo(quote).label;

    return {
      reference: Number(quote?.id) || 0,
      client: quote?.Client?.client_name || "",
      agent: quote?.Client?.am_name || "",
      package: packageName,
      contractType: (getContractTypeTags(quote) || []).join(", "),
      services: PackageServices?.length || 0,
      net,
      discount,
      total,
      status: statusLabel,
      createdAt: quote?.createdAt ? new Date(quote.createdAt).getTime() : 0,
    };
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up sort-icon"></i>;
    return sortConfig.direction === "asc"
      ? <i className="bi bi-arrow-up sort-icon active"></i>
      : <i className="bi bi-arrow-down sort-icon active"></i>;
  };

  const sortedTableData = useMemo(() => {
    if (!sortConfig.key) return uniqueTableData;

    return [...uniqueTableData].sort((a, b) => {
      const av = getRowMetrics(a)[sortConfig.key];
      const bv = getRowMetrics(b)[sortConfig.key];

      const cmp = typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv));

      return sortConfig.direction === "asc" ? cmp : -cmp;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueTableData, sortConfig, activeTab]);

  const summaryData = useMemo(() => {
    const list = tableData || [];
    const net = list.reduce((acc, curr) => acc + (Number(curr.netValue) || 0), 0);
    const discount = list.reduce((acc, curr) => acc + (Number(curr.discountValue) || 0), 0);
    const total = list.reduce((acc, curr) => acc + (Number(curr.totalValue) || 0), 0);

    return {
      entries: totalRecords || list.length,
      net: formatCurrency(net),
      discount: formatCurrency(discount),
      total: formatCurrency(total),
    };
  }, [tableData, totalRecords]);


  // Builds export rows using the exact same columns/values shown in the report table
  const buildExportRows = (rows) => {
    return (rows || []).map((quote) => {
      const PackageServices = activeTab === "Quote" ? quote?.QuotePackageServices : quote?.ContractPackageServices;

      const hasNullPackage = PackageServices?.some((item) => item?.package_id === null);
      const packageItem = PackageServices?.find((item) => item?.Package);
      const packageName = (hasNullPackage || Number(quote?.package_change_status) === 1)
        ? "Custom Package"
        : (packageItem?.Package?.name || "-");

      const contractTypeTags = getContractTypeTags(quote);

      const { net, discount, total } = getRowMetrics(quote);

      const status = activeTab === "Quote" ? getQuoteStatusInfo(quote).label : getContractStatusInfo(quote).label;

      return {
        "Created On": quote?.createdAt ? DateConvertWithTime(quote.createdAt) : "-",
        "Client": `${quote?.Client?.client_name || "-"} (${quote?.BusinessType?.name || ""})`,
        "Account Manager": quote?.Client?.am_name || "-",
        "Package": packageName,
        "Contract Type": (contractTypeTags || []).join(", ") || "-",
        "Services": PackageServices?.length || 0,
        "Net": `\u00A3${roundNumber(net)}`,
        "Discount": discount === 0 ? "\u00A30" : `-\u00A3${roundNumber(discount)}`,
        "Gross (Inc. VAT)": `\u00A3${roundNumber(total)}`,
        [`${activeTab} Status`]: status,
      };
    });
  };

  // Pulls every matching record from the backend (ignoring pagination) for export
  const fetchAllReportRows = async () => {
    const req = {
      agent_id,
      page: 1,
      limit: 0,
      search: InputValue,
      type: "reports",
      ...getDateFilterParams(),
    };

    if (activeTab === "Quote") {
      req.quoteTiles = "";
      const response = await dispatch(getAllQuotes(req)).unwrap();
      return response?.status ? response.data : [];
    }

    req.contractTiles = "";
    const response = await dispatch(getAllContracts(req)).unwrap();
    return response?.status ? response.data : [];
  };

  // Whenever a status filter (other than "All") is selected, fetch every row for the
  // current tab/search so the filter isn't limited to rows already loaded via infinite scroll
  useEffect(() => {
    if (statusValue === "All") {
      setStatusFilterData([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await fetchAllReportRows();
        if (!cancelled) setStatusFilterData(rows || []);
      } catch (error) {
        if (!cancelled) setStatusFilterData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusValue, activeTab, InputValue, currentDateFilter, range]);

  const getExportRows = async () => {
    const rows = await fetchAllReportRows();
    const filtered = statusValue === "All" ? rows : (rows || []).filter((quote) => matchesStatusFilter(quote, activeTab, statusValue));
    return buildExportRows(filtered);
  };

  const downloadCSV = (data, filename) => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [];
    // HEADER
    csvRows.push(headers.join(","));
    // BODY
    data.forEach((row) => {
      const values = headers.map((header) => {
        let value = row[header] ?? "";
        // double quotes escape
        value = String(value).replace(/"/g, '""');
        return `"${value}"`;
      });
      csvRows.push(values.join(","));
    });
    const csvString = csvRows.join("\n");
    // UTF-8 BOM ADD
    const csvWithBom = "\uFEFF" + csvString;
    const blob = new Blob(
      [csvWithBom],
      {
        type: "text/csv;charset=utf-8;",
      }
    );
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const exportRows = await getExportRows();
      if (exportRows.length === 0) {
        Swal.fire({ icon: "warning", title: "No Data", text: "No data to export!" });
        return;
      }
      downloadCSV(exportRows, `${activeTab}_Report.csv`);
    } catch (error) {
      Swal.fire({ icon: "warning", title: "No Data", text: "No data to export!" });
    } finally {
      setLoading(false);
    }
  };

  
  const handleExportPDF = async () => {
    setLoading(true);
    try {
      const exportRows = await getExportRows();
      if (exportRows.length === 0) {
        Swal.fire({ icon: "warning", title: "No Data", text: "No data to export!" });
        return;
      }

      const pdfColumns = Object.keys(exportRows[0]).map((key) => ({ label: key, key }));

      const res = await axios.post(
        Config.base_url + "export-contract-pdf",
        { data: exportRows, columns: pdfColumns },
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${activeTab}_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire({ icon: "warning", title: "No Data", text: "No data to export!" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loading) {
      if (isSearchFetch.current) {
        isSearchFetch.current = false;
        return;
      }
      Swal.fire({
        title: "",
        html: '<div style="height:250px;"><div class="loader" style="margin:0px auto;"></div><p class="text-white mt-3">Please Wait...</p></div>',
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        background: "transparent",
        backdrop: "rgba(229, 233, 236, 0.2)",

        customClass: {
          popup: "no-shadow",
        },
      });
    } else {
      Swal.close();
    }
  }, [loading]);

  return (
    <div className="quote-report-wrapper">
      <div className="quote-report-container">
        {/* HEADER */}
        <div className="report-header">
          <div className="report-heading-row">
            <div>
              <div className="report-label">
                <i className="bi bi-file-earmark-bar-graph"></i>
                REPORTS
              </div>

              <h1 className="report-title">{activeTab} Report</h1>

              <p className="report-description">
                Expand a row to see services, payment types, contract types and discounts.
              </p>

              <div className="gap-3">

              <div className="report-tabs">
                <button
                  className={`report-tab${activeTab === "Quote" ? " active" : ""}`}
                  onClick={() => handleTabChange("Quote")}
                >
                  Quote
                </button>
                <button
                  className={`report-tab${activeTab === "Contract" ? " active" : ""}`}
                  onClick={() => handleTabChange("Contract")}
                >
                  Contract
                </button>
              </div>


              <div className="col-lg-3 filter-select  select_tab_report">
                <select
                  onChange={handleDateChange}
                  value={currentDateFilter}
                  className="form-select"
                >
                  <option value="current-month">Current Month</option>
                  <option value="prev-month">Previous Month</option>
                  <option value="current-quarter">Current Quarter</option>
                  <option value="previous-quarter">Previous Quarter</option>
                  <option value="current-calender-quarter">
                    Current Calendar Year
                  </option>
                  <option value="prev-calender-year">Previous Calendar Year</option>
                  <option value="custom-date">Custom Date</option>
                </select>
              </div>

              {currentDateFilter === "custom-date" && (
                <div className="  gap-2  mb-3 col-lg-3 custom-datefilter select_tab_report">
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={["DateRangePicker"]}>
                      <DatePicker.RangePicker
                        value={range}
                        onChange={handleRangeChange}
                        placeholder={['Start Date', 'End Date']}
                        format="DD/MM/YYYY"
                        className=""
                      />

                    </DemoContainer>
                  </LocalizationProvider>
                </div>
              )}
             </div>


            </div>

            {/* EXPORT */}
            <div className="export-buttons">

              <button
                type="button"
                className="email-export-btn"
                onClick={handleExportPDF}
                disabled={pdfExporting || csvExporting}
              >
                <i className="ri-download-2-line"></i>
                Export PDF
              </button>

              <button
                type="button"
                className="email-export-btn"
                onClick={handleExport}
                disabled={pdfExporting || csvExporting}
              >
                <i className="ri-download-2-line"></i>
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="summary-grid">
          <div className="summary-card-new">
            <div className="summary-label-new">Total Records</div>
            <div className="summary-value-new">{cardData?.entry || 0}</div>
          </div>

          <div className="summary-card-new">
            <div className="summary-label-new">Total (Annual)</div>
            <div className="summary-value-new">£{formatNumber(roundNumber(Number(cardData?.grossAmmount)))}</div>
          </div>

          <div className="summary-card-new">
            <div className="summary-label-new">Total (Monthly)</div>
            <div className="summary-value-new">£{formatNumber(roundNumber(Number(cardData?.grossAmmount) / 12))}</div>
          </div>

          <div className="summary-card-new discount">
            <div className="summary-label-new">Discount</div>
            <div className="summary-value-new">£{formatNumber(roundNumber(roundNumber(Number(cardData?.grossAmmount)) - roundNumber(Number(cardData?.netAmmount))))}</div>
          </div>

          <div className="summary-card-new">
            <div className="summary-label-new">Net (Annual)</div>
            <div className="summary-value-new">£{formatNumber(roundNumber(Number(cardData?.netAmmount)))}</div>
          </div>

          <div className="summary-card-new">
            <div className="summary-label-new">Net (Monthly)</div>
            <div className="summary-value-new">£{formatNumber(roundNumber(roundNumber(Number(cardData?.netAmmount)) / 12))}</div>
          </div>

          {
            cardData?.vat_registered === 1 ?
              <>
                <div className="summary-card-new total">
                  <div className="summary-label-new">Gross (inc. VAT, Annual)</div>
                  <div className="summary-value-new">£{formatNumber(roundNumber(Number(cardData?.includingVat)))}</div>
                </div>


                <div className="summary-card-new total">
                  <div className="summary-label-new">Gross (inc. VAT, Monthly)</div>
                  <div className="summary-value-new">£{formatNumber(roundNumber(Number(cardData?.includingVat) / 12))}</div>
                </div>
              </>
              : ""
          }


        </div>


        {/* SEARCH */}
        <div className="search-filter-bar">
          <div className="search-box-new">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Search here"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>

          <div className="filter-label">
            <select
              className="status-filter-new"
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
            >
              <option value="All">All {activeTab.charAt(0).toUpperCase() + activeTab.slice(1).toLowerCase()} Status</option>
              {activeTab === "Quote"
                ? QUOTE_STATUS_BUCKETS.map((bucket) => (
                  <option key={bucket} value={bucket}>{bucket}</option>
                ))
                : CONTRACT_STATUS_BUCKETS.map(({ label }) => (
                  <option key={label} value={label}>{label}</option>
                ))}
            </select>
          </div>
        </div>

        {/* TABLE */}
        <div className="report-table-wrapper" style={{ position: "relative" }}>

          {/* {loading && tableData.length === 0 && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.65)",
                zIndex: 5,
              }}
            >
              <div className="loader"></div>
            </div>
          )} */}

          <div className="report-table-scroll" onScroll={handleScroll}>
            <table className="quote-table">
              <thead>
                <tr>
                  <th className="expand-cell"></th>
                  {/* <th className="sortable-th" onClick={() => handleSort("reference")}>
                    Reference {renderSortIcon("reference")}
                  </th> */}
                  <th className="sortable-th" onClick={() => handleSort("createdAt")}>
                    Created On {renderSortIcon("createdAt")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSort("client")}>
                    Client {renderSortIcon("client")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSort("agent")}>
                   Account Manager {renderSortIcon("agent")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSort("package")}>
                    Package {renderSortIcon("package")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSort("contractType")}>
                    Contract Type {renderSortIcon("contractType")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSort("services")}>
                    Services {renderSortIcon("services")}
                  </th>
                  <th className="text-end sortable-th" onClick={() => handleSort("net")}>
                    Net {renderSortIcon("net")}
                  </th>
                  <th className="text-end sortable-th" onClick={() => handleSort("discount")}>
                    Discount {renderSortIcon("discount")}
                  </th>
                  <th className="text-end sortable-th" onClick={() => handleSort("total")}>
                    Gross (Inc. VAT) {renderSortIcon("total")}
                  </th>
                  <th className="sortable-th" onClick={() => handleSort("status")}>
                    {activeTab} Status {renderSortIcon("status")}
                  </th>
                </tr>
              </thead>

              <tbody>
                {sortedTableData?.map((quote, index) => {

                  const id = quote.id;

                  const PackageServices = activeTab === "Quote" ? quote?.QuotePackageServices : quote?.ContractPackageServices;

                  const isExpanded = expandedId === id;

                  const createdAt = quote.createdAt;

                  const contractTypeTags = getContractTypeTags(quote);


                  const totalamount = PackageServices?.reduce((sum, item) => (
                    Number(item?.service_type) !== 1
                      ? sum
                      : sum + (item?.price)
                  ), 0)
                  const totalamountWithDiscount = totalamount > 0 ? (totalamount - (totalamount * quote?.discount_ongoing) / 100) : 0

                  const totalamountOneOff = PackageServices?.reduce((sum, item) => (
                    Number(item?.service_type) !== 0
                      ? sum
                      : sum + (item?.price)
                  ), 0)
                  const totalamountWithDiscountOneOff = totalamountOneOff > 0 ? (totalamountOneOff - (totalamountOneOff * quote?.discount_oneoff) / 100) : 0

                  const totalDiscountAmount = roundNumber(totalamountWithDiscount) + roundNumber(totalamountWithDiscountOneOff)


                  return (
                    <React.Fragment key={id ?? index}>
                      <tr
                        className={`quote-main-row${isExpanded ? " expanded" : ""}${PackageServices?.length === 0 ? " collapsed-row" : ""
                          }`}
                      >
                        <td className="expand-cell">
                          <button
                            className="expand-btn"
                            onClick={() => toggleQuote(PackageServices, id)}
                          >
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </td>
{/* 
                        <td>
                          <div className="reference">{id}</div>
                        </td> */}

                        <td>
                          <div className="created">{DateConvertWithTime(createdAt)}</div>
                        </td>

                        <td>
                          <div className="client-name">{quote?.Client?.client_name}</div>
                          <div className="muted-line">{quote?.BusinessType?.name || ""}</div>
                        </td>

                        <td>
                          <div className="agent-name">
                            {
                              quote?.Client?.am_name ? (
                                <span className="agent-name">{quote?.Client?.am_name}</span>
                              ) : (
                                <span className="agent-name">-</span>
                              )
                            }

                          </div>
                        </td>

                        <td>
                          <div className="package">
                            {(() => {
                              const hasNullPackage = PackageServices?.some(
                                (item) => item?.package_id === null
                              );

                              if (hasNullPackage || Number(quote?.package_change_status) === 1) {
                                return "Custom Package";
                              }

                              const packageItem = PackageServices?.find((item) => item?.Package);
                              return packageItem?.Package?.name || "-";
                            })()}
                          </div>
                        </td>

                        <td>
                          <div className="contract-tags">
                            {(contractTypeTags || []).map((tag) => (
                              <span className="contract-tag" key={tag}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        </td>

                        <td>
                          <span className="service-count">{PackageServices?.length}</span>
                        </td>

                        <td className="amount-new">
                          £{totalDiscountAmount}
                          <div className="muted-line">
                            £{roundNumber(
                              totalDiscountAmount / 12
                            )}/mo
                          </div>
                        </td>

                        <td className="discount-value">

                          {(() => {
                            const subtotalValue = PackageServices?.reduce((sum, item) => (
                              sum + (item?.price)
                            ), 0) || 0;
                            const totalDiscount = roundNumber(subtotalValue) - totalDiscountAmount
                            return totalDiscount === 0 ? "£0" : `-£${roundNumber(totalDiscount)}`;
                          })()}
                        </td>

                        <td className="amount-new">

                          {
                            quote?.Agent?.vat_registered?.toUpperCase() === "YES" ?
                              <>
                                <div>£{roundNumber(
                                  totalDiscountAmount
                                  +
                                  roundNumber(totalDiscountAmount * 0.20)
                                )}</div>

                                <div className="muted-line">
                                  £{roundNumber(
                                    (totalDiscountAmount
                                      +
                                      roundNumber(totalDiscountAmount * 0.20)) / 12
                                  )}/mo

                                </div>
                              </>

                              :
                              <>
                                <div>£{totalDiscountAmount}</div>

                                <div className="muted-line">
                                  £{roundNumber(
                                    totalDiscountAmount / 12
                                  )}/mo

                                </div>

                              </>
                          }




                        </td>

                        <td>
                          {(() => {
                            const { label, bucket } =
                              activeTab === "Quote"
                                ? getQuoteStatusInfo(quote)
                                : getContractStatusInfo(quote);
                            return (
                              <span className={`status ${getStatusColor(bucket)}`}>
                                {label}
                              </span>
                            );
                          })()}
                        </td>
                      </tr>

                      {/* EXPANDED ROW */}
                      {PackageServices?.length > 0 && (
                        <tr
                          className={`expanded-content${isExpanded ? " show" : ""}`}
                          style={{
                            display: isExpanded ? "table-row" : "none",
                          }}
                        >
                          <td colSpan={11}>
                            <div className="expanded-inner">
                              <div className="expanded-grid">
                                {/* LEFT */}
                                <div>
                                  <div className="breakdown-heading">
                                    Services Breakdown
                                  </div>
                                  {/* SERVICES */}
                                  <div className="service-box">
                                    <table className="service-table">
                                      <thead>
                                        <tr>
                                          <th>Service</th>
                                          <th>Payment Type</th>
                                          <th>Contract Type</th>
                                          <th>Discount</th>
                                          <th className="text-end">Discounted Amount</th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {PackageServices?.map((item) => {

                                          if (item?.inputValueHour === 0) {
                                            return;
                                          }

                                          return (
                                            <tr key={item?.Service?.name} style={{ borderBottom: "1px solid #f0e3ee" }}>
                                              <td className="service-name">
                                                {item?.Service?.name}
                                                {
                                                  item?.inputValueHour != null && Number(item?.inputValueHour) > 0 &&
                                                  <>
                                                    <span>
                                                      ({item?.inputValueHour} hours)
                                                    </span>

                                                    <span className="payment-badge">
                                                      Hourly
                                                    </span>
                                                  </>


                                                }
                                              </td>
                                              <td>
                                                <span className="payment-badge">
                                                  {

                                                    item?.inputValueHour != null && Number(item?.inputValueHour) > 0
                                                      ?
                                                      "On Invoice"
                                                      :

                                                      Number(item?.service_type) === 1 ?
                                                        paymentDivisorsFreguencyOngoing[quote?.payment_term_ongoing]
                                                        :
                                                        paymentDivisorsFreguencyOngoing[quote?.payment_term_oneoff]


                                                  }
                                                </span>
                                              </td>
                                              <td>
                                                <span className="type-badge">
                                                  {getServiceTypeLabel(quote, item?.service_type)}
                                                </span>
                                              </td>
                                              <td className="discount-report-color">
                                                {(() => {
                                                  const diff = (item?.price) - (item?.approval_price ?? item?.price_afterdiscount);
                                                  return `${diff === 0 ? "" : "-"}£${roundNumber(diff)}`;
                                                })()}
                                              </td>

                                              <td className="text-end">
                                                £{roundNumber(item?.approval_price ?? item?.price_afterdiscount)}
                                              </td>
                                            </tr>
                                          )
                                        })
                                        }

                                        <tr className="subtotal-row" style={{ borderBottom: "1px solid #f0e3ee" }}>

                                          <td colSpan={4}>Net Total (Discounted, Annual)</td>
                                          <td className="text-end">
                                            £{totalDiscountAmount}
                                          </td>

                                        </tr>
                                        <tr>
                                          <td colSpan={4} className="text-muted">Net Total (Discounted, Monthly)</td>
                                          <td className="text-end" style={{ borderTop: "1px solid #f0e3ee", borderBottom: "1px solid #f0e3ee" }}>
                                            £{roundNumber(
                                              PackageServices?.reduce((sum, item) => (
                                                item?.inputValueHour === 0
                                                  ? sum
                                                  : sum + (item?.approval_price ?? item?.price_afterdiscount)
                                              ), 0) / 12
                                            )}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  <div className="mt-4">
                                    <div className="breakdown-heading">Time-based services — billed on invoice</div>
                                    <p className="breakdown-para-report">These amounts are excluded from the Gross, Net, Discount and Total figures and are invoiced separately
                                    </p>
                                  </div>

                                  {/* Hourly Services */}
                                  {
                                    PackageServices
                                      ?.filter((item) => item?.inputValueHour === 0)
                                      ?.length > 0 &&
                                    (
                                      <div className="service-box">
                                        <table className="service-table">
                                          <thead>
                                            <tr>
                                              <th>Service</th>
                                              <th>Payment Type</th>
                                              <th>Contract Type</th>
                                              <th className="text-end">Value</th>
                                            </tr>
                                          </thead>

                                          <tbody>
                                            {PackageServices
                                              ?.filter((item) => item?.inputValueHour === 0)
                                              ?.map((item) => {

                                                return (
                                                  <tr key={item?.Service?.name} style={{ borderBottom: "1px solid #f0e3ee" }}>
                                                    <td className="service-name">
                                                      {item?.Service?.name}
                                                    </td>
                                                    <td>
                                                      <span className="payment-badge">
                                                        On Invoice
                                                      </span>
                                                    </td>
                                                    <td>
                                                      <span className="type-badge">
                                                        Hourly basis
                                                      </span>
                                                    </td>

                                                    <td className="text-end">
                                                      £{roundNumber(item?.rate ?? item?.exist_price_rate)}
                                                    </td>
                                                  </tr>
                                                )
                                              })
                                            }
                                          </tbody>
                                        </table>
                                      </div>
                                    )
                                  }



                                  {/* DISCOUNT */}

                                  <div className="report-discount-box">
                                    <div className="report-discount-box-inner">
                                      <div className="report-discount-box-left">
                                        <p className="report-discount-box-title">Discount (applied once to the entry)</p>
                                      </div>
                                      <div className="report-discount-box-right">
                                        <p className="report-discount-box-amount">Discount amount</p>
                                      </div>
                                    </div>
                                    <div className="report-discount-box-inner">
                                      <div className="report-discount-box-left-1">
                                        <p className="report-discount-box-title-1">Discount on gross <span>


                                          {(() => {
                                            const subtotalValue = PackageServices?.reduce((sum, item) => (
                                              sum + (item?.price)
                                            ), 0) || 0;
                                            return `£${roundNumber(subtotalValue).toLocaleString('en-GB')}`;
                                          })()}
                                        </span>
                                          {
                                            quote?.approver_name ?
                                              <>
                                                · Approver : <span>{quote?.approver_name}</span>
                                              </>
                                              : ""
                                          }


                                        </p>
                                      </div>
                                      <div className="report-discount-box-right-1">
                                        <p className="report-discount-box-amount-1">

                                          {(() => {
                                            const subtotalValue = PackageServices?.reduce((sum, item) => (
                                              sum + (item?.price)
                                            ), 0) || 0;
                                            const totalDiscount = roundNumber(subtotalValue) - totalDiscountAmount
                                            return totalDiscount === 0 ? "£0" : `-£${roundNumber(totalDiscount)}`;
                                          })()}
                                        </p>
                                      </div>
                                    </div>
                                  </div>

                                </div>

                                {/* RIGHT */}
                                <div>
                                  {/* MULTIPLIERS */}
                                  <div className="side-card">
                                    <div className="side-card-title">Multipliers</div>
                                    <div className="side-row">
                                      <span>Complexity & Risk</span>
                                      <span>{withDefaultLabel(quote?.complexity_label, "Zero")}</span>
                                    </div>
                                    <div className="side-row">
                                      <span>Quality of Data</span>
                                      <span>{getQualityDataLabel(quote?.quality_data_label)}</span>
                                    </div>
                                    <div className="side-row">
                                      <span>Meeting Frequency</span>
                                      <span>{withDefaultLabel(quote?.frequency_meeting_label, "Zero/Minimal")}</span>
                                    </div>
                                  </div>

                                  {/* TOTALS */}
                                  <div className="side-card">
                                    <div className="side-card-title">Totals</div>

                                    {/* GROSS  ANNUAL*/}
                                    <div className="side-row">
                                      <span>Total (Annual)</span>
                                      <span>
                                        {(() => {
                                          const subtotalValue = PackageServices?.reduce((sum, item) => (
                                            sum + (item?.price)
                                          ), 0) || 0;
                                          return `£${roundNumber(subtotalValue).toLocaleString('en-GB')}`;
                                        })()}
                                      </span>
                                    </div>

                                    {/* GROSS  MONTHLY*/}
                                    <div className="side-row">
                                      <span>Total (Monthly)</span>
                                      <span>{(() => {
                                        const subtotalValue = PackageServices?.reduce((sum, item) => (
                                          sum + (item?.price)
                                        ), 0) || 0;
                                        return `£${roundNumber((roundNumber(subtotalValue)) / 12).toLocaleString('en-GB')}`;
                                      })()}</span>
                                    </div>

                                    {/* DISCOUNT */}
                                    <div className="side-row">
                                      <span>Discount</span>
                                      <span>
                                        {(() => {
                                          const subtotalValue = PackageServices?.reduce((sum, item) => (
                                            sum + (item?.price)
                                          ), 0) || 0;
                                          const totalDiscount = roundNumber(subtotalValue) - totalDiscountAmount
                                          return totalDiscount === 0 ? "£0" : `-£${roundNumber(totalDiscount)}`;
                                        })()}

                                      </span>
                                    </div>

                                    {/* PREMIUM */}
                                    {/* <div className="side-row">
                                      <span>Premium</span>
                                      <span>£0</span>
                                    </div> */}

                                    {/* NET ANNUAL */}
                                    <div className="side-row">
                                      <span>Net (Annual)</span>
                                      <span> £{totalDiscountAmount}</span>
                                    </div>

                                    {/* NET MONTHLY */}
                                    <div className="side-row">
                                      <span>Net (Monthly)</span>
                                      <span>
                                        £{roundNumber(totalDiscountAmount / 12)
                                        }</span>
                                    </div>

                                    {/* VAT 20*/}
                                    <div className="side-row">
                                      <span>VAT (20%)
                                      </span>


                                      {
                                        quote?.Agent?.vat_registered?.toUpperCase() === "YES" ?
                                          <span>£{roundNumber(totalDiscountAmount * 0.20)}</span>
                                          : 0
                                      }




                                    </div>

                                    {/* TOTAL ANNUAL */}
                                    <div className="side-row total-row">
                                      <span>Gross (Annual)</span>
                                      {
                                        quote?.Agent?.vat_registered?.toUpperCase() === "YES" ?
                                          <span>£{totalDiscountAmount
                                            +
                                            roundNumber(totalDiscountAmount * 0.20)
                                          }</span>
                                          :
                                          <span>£{totalDiscountAmount}</span>

                                      }



                                    </div>

                                    {/* TOTAL MONTHLY */}
                                    <div className="side-row total-row">
                                      <span>Gross (Monthly)</span>


                                      {
                                        quote?.Agent?.vat_registered?.toUpperCase() === "YES" ?
                                          <span>£{
                                            roundNumber((totalDiscountAmount
                                              +
                                              roundNumber(totalDiscountAmount * 0.20)) / 12)
                                          }</span>
                                          :
                                          <span>£{roundNumber(totalDiscountAmount / 12)}</span>
                                      }

                                    </div>
                                  </div>

                                  {/* SALES */}
                                  <div className="side-card">
                                    <div className="side-card-title">Sales & Contract</div>
                                    <div className="side-row">
                                      <span>Sales Person</span>
                                      <span>{quote?.User?.user_fname + " " + quote?.User?.user_lname}</span>
                                    </div>

                                    {oldData && Object.keys(oldData).length > 0 && (
                                      <div className="side-row">
                                        <span>Old {activeTab === "Quote" ? "Quote" : "Contract"}</span>
                                        <span>£{roundNumber(
                                          (activeTab === "Quote"
                                            ? oldData?.ExistClientContract?.QuotePackageServices
                                            : oldData?.ExistClientContract?.ContractPackageServices
                                          )?.reduce((sum, item) => (
                                            item?.inputValueHour === 0
                                              ? sum
                                              : sum + roundNumber(item?.approval_price ?? item?.price_afterdiscount)
                                          ), 0)
                                        )}</span>
                                      </div>
                                    )}

                                    <div className="side-row">
                                      <span>New {activeTab === "Quote" ? "Quote" : "Contract"}</span>
                                      <span>£{roundNumber(
                                        PackageServices?.reduce((sum, item) => (
                                          item?.inputValueHour === 0
                                            ? sum
                                            : sum + (item?.approval_price ?? item?.price_afterdiscount)
                                        ), 0)
                                      )}</span>
                                    </div>

                                    <div className="side-row">
                                      <span>Created</span>
                                      <span>{quote?.createdAt?.split("T")[0]}</span>
                                    </div>

                                    <div className="side-row">
                                      <span>Sent</span>
                                      <span>{oldData?.sentRecord?.createdAt?.split("T")[0] || '-'}</span>
                                    </div>

                                  </div>

                                  {/* NOTES */}
                                  {
                                    quote?.package_description && (
                                      <div className="side-card">
                                        <div className="side-card-title">Notes</div>
                                        <div className="notes-text">{stripHtml(quote?.package_description)}</div>
                                      </div>
                                    )
                                  }

                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}


                    </React.Fragment>
                  );
                })}

                {!loading && sortedTableData?.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center py-5">
                      {/* No data found */}
                      <div className="text-center">
                        <img
                          src={noDataImage}
                          alt="No records available"
                          style={{ width: '250px', height: 'auto', objectFit: 'contain' }}
                        />
                        <p className='fs-16'>There are no records to display</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
